import { useConvex } from "convex/react"
import type { FunctionReturnType } from "convex/server"
import { useCallback, useEffect, useMemo, useState } from "react"
import { api } from "~convex/_generated/api"

type PaginatedStatus = "LoadingFirstPage" | "LoadingMore" | "CanLoadMore" | "Exhausted"

type PageLoader<T> = (
	cursor: string | null,
	numItems: number,
) => Promise<{ page: T[]; isDone: boolean; continueCursor: string }>

interface PaginatedResult<T> {
	results: T[]
	status: PaginatedStatus
	loadMore: (numItems: number) => void
}

/**
 * Generic non-reactive paginated state machine. Resets and fetches the first
 * page whenever `loader` changes (so consumers must memoize their loader with
 * stable deps). Calls into `loader` once per first-page or load-more action,
 * never subscribes reactively.
 */
function usePaginated<T>(
	loader: PageLoader<T> | null,
	initialNumItems: number,
): PaginatedResult<T> {
	const [results, setResults] = useState<T[]>([])
	const [cursor, setCursor] = useState<string | null>(null)
	const [status, setStatus] = useState<PaginatedStatus>("LoadingFirstPage")

	useEffect(() => {
		if (!loader) {
			setResults([])
			setCursor(null)
			setStatus("Exhausted")
			return
		}

		let cancelled = false
		setResults([])
		setCursor(null)
		setStatus("LoadingFirstPage")

		loader(null, initialNumItems)
			.then(result => {
				if (cancelled) {
					return
				}
				setResults(result.page)
				setCursor(result.continueCursor)
				setStatus(result.isDone ? "Exhausted" : "CanLoadMore")
			})
			.catch(() => {
				if (cancelled) {
					return
				}
				setResults([])
				setStatus("Exhausted")
			})

		return () => {
			cancelled = true
		}
	}, [loader, initialNumItems])

	const loadMore = useCallback(
		(numItems: number) => {
			if (!loader || status !== "CanLoadMore") {
				return
			}
			setStatus("LoadingMore")
			loader(cursor, numItems)
				.then(result => {
					setResults(prev => [...prev, ...result.page])
					setCursor(result.continueCursor)
					setStatus(result.isDone ? "Exhausted" : "CanLoadMore")
				})
				.catch(() => {
					setStatus("CanLoadMore")
				})
		},
		[loader, status, cursor],
	)

	return { results, status, loadMore }
}

type UserPostByHandle = FunctionReturnType<typeof api.posts.getUserPostsByHandle>["page"][number]

/**
 * Non-reactive replacement for usePaginatedQuery(api.posts.getUserPostsByHandle, ...).
 * Fetches once per (handle, enabled) change instead of subscribing — profile
 * post lists don't need to live-update from posts table churn, and the
 * reactive re-eval was a major Convex cost driver.
 */
export function useUserPostsByHandle(
	handle: string | null,
	enabled: boolean,
	initialNumItems = 20,
): PaginatedResult<UserPostByHandle> {
	const convex = useConvex()
	const loader = useMemo<PageLoader<UserPostByHandle> | null>(
		() =>
			enabled && handle
				? (cursor, numItems) =>
						convex.query(api.posts.getUserPostsByHandle, {
							handle,
							paginationOpts: { numItems, cursor },
						})
				: null,
		[convex, enabled, handle],
	)
	return usePaginated(loader, initialNumItems)
}
